/**
 * Grab-to-scroll (clic-glissé souris) pour la rangée de pills catégories.
 *
 * La liste `.lms-category-pills__list` est déjà scrollable nativement
 * (molette/trackpad + tactile). Ce composant ajoute uniquement le drag à la
 * SOURIS sur desktop — le scroll natif tactile du mobile est laissé intact
 * (on n'intercepte que les pointeurs `mouse`).
 *
 * @class
 * @extends HTMLElement
 */
if (!customElements.get('lms-category-pills')) {
	customElements.define(
		'lms-category-pills',
		class LmsCategoryPills extends HTMLElement {
			// Selectors
			#selectors = {
				list: '.lms-category-pills__list',
				link: '.lms-category-pills__pill',
			};

			// CSS classes
			#classes = {
				grabbing: 'is-grabbing',
				draggable: 'is-draggable',
			};

			/** @type {ResizeObserver|null} @private */
			#resizeObserver = null;

			// Distance (px) au-delà de laquelle un mouvement est considéré comme un
			// drag → on supprime le clic pour ne pas déclencher le lien.
			#dragThreshold = 6;

			// Cached elements
			#elements = {};

			/** @type {boolean} @private */
			#isDown = false;
			/** @type {number} @private */
			#startX = 0;
			/** @type {number} @private */
			#startScrollLeft = 0;
			/** @type {boolean} @private */
			#hasDragged = false;
			/** @type {number} @private */
			#targetScrollLeft = 0;
			/** @type {number|null} @private */
			#rafId = null;

			/**
			 * Lifecycle - Component connected to DOM
			 * @private
			 */
			connectedCallback() {
				this.#cacheElements();
				if (!this.#isValidSetup()) return;
				this.#attachEventListeners();
				this.#observeResize();
				this.#updateDraggable();
			}

			/**
			 * Lifecycle - Component removed from DOM
			 * @private
			 */
			disconnectedCallback() {
				this.#removeEventListeners();
				this.#resizeObserver?.disconnect();
				this.#resizeObserver = null;
				if (this.#rafId !== null) {
					cancelAnimationFrame(this.#rafId);
					this.#rafId = null;
				}
			}

			/**
			 * Cache DOM elements
			 * @private
			 */
			#cacheElements() {
				this.#elements = {
					list: this.querySelector(this.#selectors.list),
				};
			}

			/**
			 * Validate required elements exist
			 * @private
			 * @returns {boolean}
			 */
			#isValidSetup() {
				if (!this.#elements.list) {
					console.error('LmsCategoryPills: list element not found');
					return false;
				}
				return true;
			}

			/**
			 * Attach event listeners
			 * @private
			 */
			#attachEventListeners() {
				const { list } = this.#elements;
				list.addEventListener('pointerdown', this.#handlePointerDown);
				list.addEventListener('pointermove', this.#handlePointerMove);
				list.addEventListener('pointerup', this.#handlePointerUp);
				list.addEventListener('pointercancel', this.#handlePointerUp);
				// Bloque le drag natif des liens/images (sinon presser SUR une card
				// démarre un drag-and-drop du navigateur au lieu de scroller).
				list.addEventListener('dragstart', this.#handleDragStart);
				// Supprime le clic « fantôme » qui suit un vrai drag (sinon on suit le lien).
				list.addEventListener('click', this.#handleClickCapture, true);
			}

			/**
			 * Remove event listeners
			 * @private
			 */
			#removeEventListeners() {
				const { list } = this.#elements;
				if (!list) return;
				list.removeEventListener('pointerdown', this.#handlePointerDown);
				list.removeEventListener('pointermove', this.#handlePointerMove);
				list.removeEventListener('pointerup', this.#handlePointerUp);
				list.removeEventListener('pointercancel', this.#handlePointerUp);
				list.removeEventListener('dragstart', this.#handleDragStart);
				list.removeEventListener('click', this.#handleClickCapture, true);
			}

			/**
			 * True si la liste déborde horizontalement (drag utile).
			 * @private
			 * @returns {boolean}
			 */
			#isScrollable() {
				const { list } = this.#elements;
				return list.scrollWidth > list.clientWidth + 1;
			}

			/**
			 * Observe les changements de taille pour (dé)activer le curseur grab.
			 * @private
			 */
			#observeResize() {
				if (typeof ResizeObserver === 'undefined') return;
				this.#resizeObserver = new ResizeObserver(() => this.#updateDraggable());
				this.#resizeObserver.observe(this.#elements.list);
			}

			/**
			 * Reflète l'état « draggable » (overflow présent) sur la liste.
			 * @private
			 */
			#updateDraggable() {
				this.#elements.list.classList.toggle(this.#classes.draggable, this.#isScrollable());
			}

			/**
			 * Démarre le drag (souris uniquement, si overflow).
			 * @private
			 * @param {PointerEvent} event
			 */
			#handlePointerDown = (event) => {
				if (event.pointerType !== 'mouse') return; // tactile/pen = scroll natif
				if (event.button !== 0) return; // clic gauche seulement
				if (!this.#isScrollable()) return;

				this.#isDown = true;
				this.#hasDragged = false;
				this.#startX = event.clientX;
				this.#startScrollLeft = this.#elements.list.scrollLeft;
				this.#elements.list.classList.add(this.#classes.grabbing);
				this.#elements.list.setPointerCapture?.(event.pointerId);
			};

			/**
			 * Applique le déplacement au scrollLeft.
			 * @private
			 * @param {PointerEvent} event
			 */
			#handlePointerMove = (event) => {
				if (!this.#isDown) return;
				const walk = event.clientX - this.#startX;
				if (Math.abs(walk) > this.#dragThreshold) this.#hasDragged = true;
				this.#targetScrollLeft = this.#startScrollLeft - walk;
				// Batch les écritures scrollLeft sur la frame pour éviter les saccades
				if (this.#rafId === null) {
					this.#rafId = requestAnimationFrame(this.#applyScroll);
				}
			};

			/**
			 * Applique le scroll cible (appelé sur la frame d'animation).
			 * @private
			 */
			#applyScroll = () => {
				this.#rafId = null;
				this.#elements.list.scrollLeft = this.#targetScrollLeft;
			};

			/**
			 * Termine le drag.
			 * @private
			 * @param {PointerEvent} event
			 */
			#handlePointerUp = (event) => {
				if (!this.#isDown) return;
				this.#isDown = false;
				if (this.#rafId !== null) {
					cancelAnimationFrame(this.#rafId);
					this.#rafId = null;
				}
				this.#elements.list.classList.remove(this.#classes.grabbing);
				this.#elements.list.releasePointerCapture?.(event.pointerId);
			};

			/**
			 * Empêche le drag-and-drop natif des liens/images pendant un drag souris.
			 * @private
			 * @param {DragEvent} event
			 */
			#handleDragStart = (event) => {
				event.preventDefault();
			};

			/**
			 * Bloque le clic déclenché à la fin d'un vrai drag.
			 * @private
			 * @param {MouseEvent} event
			 */
			#handleClickCapture = (event) => {
				if (!this.#hasDragged) return;
				const link = event.target.closest(this.#selectors.link);
				if (!link) return;
				event.preventDefault();
				event.stopPropagation();
				this.#hasDragged = false;
			};
		}
	);
}
